<template>
  <div 
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada || showQRCode }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada || showQRCode }"
      ref="formCardRef"
    >
      <!-- Vista de QR confirmado -->
      <div v-if="asistenciaConfirmada || showQRCode" class="qr-view text-center">
        <p class="qr-header"> Digital Pass</p>
        <div class="qr-wrapper">
          <img v-if="qrCode" :src="qrCode" alt="Código QR" class="qr-code">
          <p v-else class="qr-error-message">The QR code has not been generated.</p>
        </div>
    
        <p class="qr-instructions">Show it on the day of the event</p>
      </div>

      <!-- Formulario paso a paso -->
      <div v-else>
        
        <div class="form-header">
              <h2 ref="tituloAnimado" class="form-title ">Confirm your<br><span class="subtitulo">Attendance </span></h2><br>

          <div class="progress-container">
  <div
    class="progress-bar"
    :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
  ></div>
</div>
          <p class="form-subtitle">Step {{ currentStep }} of {{ totalSteps }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <!-- Paso 1: Nombre y Teléfono -->
          <div v-if="currentStep === 1" class="step-box">
            <div class="form-group">
              <label for="name" class="form-label">Full Name</label>
              <input id="name" v-model.trim="name" type="text" class="form-control" placeholder="Enter your full name" required>
            </div>
            <div class="form-group">
              <label for="phone" class="form-label">Phone</label>
              <input id="phone" v-model.trim="phone" type="tel" class="form-control" placeholder="Enter phone only number" required pattern="[0-9]{10}" title="Enter phone only number 10 digits">
            </div>
          </div>

          <!-- Paso 2: Invitado por (oculto en XV o YM) -->
          <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
            <div class="form-group">
              <label class="form-label">Invited By?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="novia" v-model="invitedBy" type="radio" value="novia" class="radio-input" required>
                  <label for="novia" class="radio-label">Bride</label>
                </div>
                <div class="radio-option">
                  <input id="novio" v-model="invitedBy" type="radio" value="novio" class="radio-input">
                  <label for="novio" class="radio-label">Groom</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 3: Asistencia -->
          <div v-if="currentStep === asistenciaStep" class="step-box">
            <div class="form-group">
              <label class="form-label">You will Attend?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="si" v-model="attending" type="radio" value="si" class="radio-input" required>
                  <label for="si" class="radio-label">Yes</label>
                </div>
                <div class="radio-option">
                  <input id="no" v-model="attending" type="radio" value="no" class="radio-input">
                  <label for="no" class="radio-label">No</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 4: Número de invitados  -->
<div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
            <div class="form-group">
              <label for="guests" class="form-label">How many Attend?</label>
              <select id="guests" v-model="guests" class="form-select" :disabled="attending === 'no' || loading" required>
                <option value="" disabled selected>Select an option</option>
                <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
                <option v-if="attending === 'no'" value="0">0</option>
              </select>
              <div v-if="loading" class="loading-indicator">Loading options...</div>
            </div>
          </div>

          <!-- Botones de navegación -->
          <div class="d-flex justify-content-between mt-3">
            <button type="button" class="submit-button" @click="prevStep" v-if="currentStep > 1">Back</button>
            <button v-if="currentStep < totalSteps" type="button" class="submit-button" @click="nextStep">Next</button>
            <button v-if="currentStep === totalSteps" type="submit" class="submit-button" :disabled="isSubmitting">
              <span v-if="!isSubmitting">Confirm</span>
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </button>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import axios from 'axios';
import baseUrl from '@/api';
import gsap from 'gsap';
import Swal from 'sweetalert2';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: { type: Number, required: true, validator: v => Number.isInteger(v) && v > 0 },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {
    const name = ref('');
    const phone = ref('');
    const invitedBy = ref('');
    const attending = ref('');
    const guests = ref('');
    const guestsOptions = ref([1, 2]);
    const loading = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');
    
    const tituloAnimado = ref(null);

    const qrCode = ref(null);
    const showQRCode = ref(false);
    const asistenciaConfirmada = ref(false);
    const currentStep = ref(1);
    const formCardRef = ref(null);

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase();
      return ['xv', 'ym'].includes(eventName);
    });

    const totalSteps = computed(() => isXvEvent.value ? 3 : 4);
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3);

  const animateStep = () => {
  nextTick(() => {
    gsap.fromTo(formCardRef.value.querySelector('.step-box'),
      { y: 50, opacity: 0 }, // Comienza 50px más abajo e invisible
      { 
        y: 0,                // Termina en su posición original
        opacity: 1, 
        duration: 0.6, 
        ease: 'power2.out',
        // Opcional: puedes añadir perspectiva para efecto 3D
        transformPerspective: 300,
        rotationX: -10       // Ligera inclinación para efecto de hoja
      });
  });
};
const nextStep = async () => {
  // Validación paso 1: Nombre y teléfono
if (currentStep.value === 1) {
  if (!name.value.trim() || !phone.value.trim()) {
    errorMessage.value = 'Please enter your name and phone only numbers .';
    return;
  
    }
  }

  // Validación paso 2 (si aplica): Invitado por
  if (currentStep.value === 2 && !isXvEvent.value) {
    if (!invitedBy.value) {
errorMessage.value = 'Please select who invited you.';
      return;
    }
  }

  // Validación paso 3: Asistencia
  if (currentStep.value === asistenciaStep.value) {
    if (!attending.value) {
errorMessage.value = 'Please select whether you will attend or not.';
      return;
    }

     if (attending.value === 'no') {
          currentStep.value = totalSteps.value;
          await nextTick();
          handleSubmit();
          return;
        }
      }

      // Último paso (si asiste): cuántos asisten
      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (guests.value == null || Number.isNaN(Number(guests.value))) {
          errorMessage.value = 'Por favor selecciona cuántas personas asistirán.';
          return;
        }
      }

      // OK -> avanzar
      errorMessage.value = '';
      if (currentStep.value < totalSteps.value) {
        currentStep.value++;
        animateStep();
      }
    };



    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
        animateStep();
      }
    };

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(`${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`);
        const numPases = response.data.num_pase;
        guestsOptions.value = numPases > 0 ? Array.from({ length: numPases }, (_, i) => i + 1) : [1, 2];
      } catch (error) {
        console.error('Error al obtener límite de invitados:', error);
errorMessage.value = 'Error loading guest options.';
      } finally {
        loading.value = false;
      }
    };

    const verificarAsistencia = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/verificar-asistencia`, {
          idInvitado: props.idInvitado
        });
        if (response.data.asistio) {
          asistenciaConfirmada.value = true;
          await obtenerCodigoQR();
        }
      } catch (error) {
        console.error('Error al verificar asistencia:', error);
      }
    };

    const obtenerCodigoQR = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/codigoqrinvitado`, { id_invitado: Number(props.idInvitado) });
        qrCode.value = response.data?.codigoqr || null;
      } catch (error) {
        console.error('Error obteniendo código QR:', error);
        errorMessage.value = 'Error al generar el código QR';
      }
    };
const handleSubmit = async () => {
  if (attending.value === 'no') guests.value = '0';

  isSubmitting.value = true;
  errorMessage.value = '';

  try {
    const formData = {
      nombre_asistencia: name.value.trim(),
      telefono_invitado: phone.value.trim(),
      invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
      asistira: attending.value,
      id_festejada: props.idFestejado,
      id_invitado: props.idInvitado,
  num_pases: Number(guests.value), // ← CORREGIDO AQUÍ
    };

    await axios.post(`${baseUrl}/asistencia`, formData);

    if (attending.value === 'no') {
      Swal.fire({
        icon: 'info',
        title: '¡Gracias por avisarnos!',
        text: 'Sentimos mucho que no puedas asistir.',
        confirmButtonColor: '#d88cbd',
      });
      asistenciaConfirmada.value = true; // Para ocultar el formulario
    } else {
      await obtenerCodigoQR();
      showQRCode.value = true;
    }
  } catch (error) {
    console.error('Error confirmando asistencia:', error);
    errorMessage.value = 'Hubo un error al confirmar. Por favor intenta nuevamente.';
  } finally {
    isSubmitting.value = false;
  }
};

    watch(attending, (newValue) => {
      if (newValue === 'no') {
        guests.value = '0';
      } else if (newValue === 'si' && guests.value === '0') {
        guests.value = '1';
      }
    });

    onMounted(() => {
      fetchGuestLimit();
      verificarAsistencia();
      animateStep();
    gsap.fromTo(tituloAnimado.value,
  { y: -150, opacity: 0, scale: 1.2 },
  {
    y: 0,
    opacity: 1,
    scale: 1,
    duration: 1.2,
    ease: 'bounce.out',
    scrollTrigger: {
      trigger: tituloAnimado.value,
      start: 'top 80%',
toggleActions: 'restart none none reset'
    }
  }
);

    });

    return {
      name, phone, invitedBy, attending, guests, guestsOptions,
      loading, isSubmitting, errorMessage, isXvEvent, asistenciaConfirmada,
      qrCode, showQRCode, currentStep, nextStep, prevStep, handleSubmit,
      totalSteps, asistenciaStep, formCardRef,  tituloAnimado // <-- ¡FALTABA ESTO!

    };
  }
};
</script>

  
  
  <style scoped>
  .container{
  min-height: 200px;
    height: auto;
  }
  .decorative-svg {
    position: absolute;
    top: 0;
    left: 50%; /* Mueve la imagen al centro */
    transform: translateX(-50%); /* Ajusta la posición exacta al centro */
    width: 60%;
    height: auto;
    margin-bottom: 20px;
  }
  
  .titulo{
  
    font-size: 38px ! important;
    color: #f0c9d3;
    font-family: 'FeelingPassionate';
  }
  .upload-container {
    
    max-width: auto;
    margin: auto;
    border-radius: 15px;
  }
  
  /* Área de subida de fotos con borde punteado */
  .upload-area {
    border: 2px dashed #2b24174e;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    border-radius: 10px;
    transition: all 0.3s ease;
  }
  
  .upload-area:hover {
    background: rgba(74, 144, 226, 0.1);
  }
  
  /* Ícono de cámara */
  .upload-icon {
    font-size: 40px;
    color: #2b2d2e79;
  }
  
  /* Vista previa de imágenes */
  .preview-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;
  }
  
  .preview-item {
    position: relative;
    width: 100px;
    height: 100px;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .preview-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .delete-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    background: red;
    color: white;
    border: none;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
  .gif-pequeno {
    width: 50px; /* Ajusta el tamaño según tus necesidades */
    height: auto; /* Mantiene la proporción del GIF */
  }
  .file-input {
    background-color: white;
    color: white;
    border: none;
    width: 100px; /* Ajusta según necesites */
    height: 30px;
    opacity: 0.01; /* Casi invisible */
  }
  .upload-btn {
  background-color: #f0c9d3;
    color: white;
    font-size: 16px;
    font-weight: bold;
    padding: 12px 20px;
    justify-content: center;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease-in-out;
    display: flex;
    align-items: center;
    gap: 8px; /* Espacio entre el ícono y el texto */
    box-shadow: 0px 4px 10px rgba(225, 219, 220, 0.3);
  }
  
  .upload-btn:hover {
    background: linear-gradient(135deg, rgba(48, 73, 93, 1), rgba(74, 93, 107, 1));
    box-shadow: 0px 6px 15px rgba(255, 92, 148, 0.5);
  }
  
  .upload-btn i {
    font-size: 20px;
  }
  
  .loader-container {
    position: fixed; /* 🔥 Se fija en la pantalla sin importar el contenedor */
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%); /* 🔥 Centra el loader perfectamente */
    
    z-index: 10000 !important; /* Se asegura de estar por encima de todo */
  
    width: 100vw; /* 🔥 Ocupa todo el ancho de la pantalla */
    height: 100vh; /* 🔥 Ocupa todo el alto de la pantalla */
  
    background: rgba(0, 0, 0, 0.7); /* Fondo oscuro semitransparente */
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
  
  
  
  
  .lottie-animation {
    width: 150px;
    height: 150px;
  }
  
  .loader-text {
    color: white;
    font-size: 20px;
    font-weight: bold;
    font-family: 'Dancing Script', cursive;
    margin-top: 15px;
  }
  
  </style>
  